import {useState} from "react";
import {Typography} from "@mui/material";
import {orange} from "@mui/material/colors";
import {Window, DoorFront} from "@mui/icons-material";
import {ElevationApp} from "../ElevationApp";
import {RibbonButton, RibbonGroup} from "./Ribbon";
import {ScreenElevationPopup} from "../../../quote/openings/ScreenElevationPopup";
import {DoorElevationPopup} from "../../../quote/openings/DoorElevationPopup";
import {api} from "../../../../../api/API";
import {Opening} from "../../../../../api/Openings";
import {QuoteOpening} from "../../../../../api/QuoteOpenings";
import {useConfirm} from "../../../../../misc/Confirm";

// legacy tab: manage the opening's old file-based screen (frame) and door elevations
// while inside the new tool. Each editor opens the corresponding legacy popup, then saves the
// legacy fields and unlinks only that spec's drw elevation in one request, so the opening falls
// back to legacy for it while the others keep the tool.
export function LegacyTab(props: {
    app: ElevationApp;
}) {
    const [editing, setEditing] = useState<false | "screen" | "door">(false);
    const opening = props.app.opening;
    const confirm = useConfirm();

    const detach = async (input: {which: "screen" | "door"; value: Opening}) => {
        const app = props.app;

        // the tool reloads after the detach, which drops unsaved edits
        if(app.dirty.value) {
            try {
                await confirm.confirm("This elevation has unsaved changes, and they will be lost. Continue?");
            } catch {
                return;
            }
        }

        const project = app.project!.id;
        // in quote mode app.opening is the quote opening (see ElevationApp.load)
        const target = app.alternative !== null
            ? {project, quoteOpening: input.value.id, legacyQuoteOpening: input.value as unknown as QuoteOpening}
            : {project, opening: input.value.id, legacyOpening: input.value};

        if(input.which === "screen") {
            await api.elevationTool.unlinkScreen(target);
        } else {
            await api.elevationTool.unlinkDoor(target);
        }

        await app.reload();
        setEditing(false);
    };

    return (
        <RibbonGroup label="Legacy Elevations">
            <RibbonButton
                icon={<Window fontSize="small" />}
                label="Edit Screen"
                disabled={!opening}
                onClick={() => setEditing("screen")}
            />

            <RibbonButton
                icon={<DoorFront fontSize="small" />}
                label="Edit Door"
                disabled={!opening}
                onClick={() => setEditing("door")}
            />

            <Typography sx={{
                alignSelf: "center",
                bgcolor: orange["300"],
                color: "black",
                fontSize: 11,
                lineHeight: 1.3,
                maxWidth: 220,
                px: 1,
                py: 0.5,
                borderRadius: 1,
            }}>
                Editing an elevation here reverts that part of the opening to its legacy
                file-based version and removes the elevation-tool link.
            </Typography>

            {editing === "screen" && opening && (
                <ScreenElevationPopup<Opening>
                    row={opening}
                    onCancel={() => setEditing(false)}
                    onDone={value => detach({which: "screen", value})}
                />
            )}

            {editing === "door" && opening && (
                <DoorElevationPopup<Opening>
                    row={opening}
                    onCancel={() => setEditing(false)}
                    onDone={value => detach({which: "door", value})}
                />
            )}
        </RibbonGroup>
    );
}
